import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';

import { toast } from '@src/components/core/toaster';
import {
  fetchAdAccountCard,
  submitAdAccountCard,
  withdrawAdAccountCard,
} from '@src/api/cardVault';

const digitsOnly = (value) => (value || '').replace(/\D/g, '');
const formatCardNumber = (value) => digitsOnly(value).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '');
const brandLabel = (brand) => {
  const map = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club', jcb: 'JCB' };
  return map[brand] || 'Card';
};

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 16 }, (_, i) => String(THIS_YEAR + i));

const EMPTY_FORM = {
  cardholder_name: '',
  card_number: '',
  exp_month: '',
  exp_year: '',
  cvc: '',
  billing_zip: '',
  authorized: false,
};

/**
 * Client-facing: submit the card that goes on the Facebook ad account.
 *
 * Fetches its own status so it can sit inside the onboarding wizard or on
 * the standalone /onboarding/:slug/ad-account-card page. Never receives the
 * card back from the server — only brand, last4 and lifecycle status.
 */
export default function AdAccountCardForm({ clientSlug, onStatusChange }) {
  const [status, setStatus] = useState(null); // null = loading
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const load = useCallback(async () => {
    if (!clientSlug) return;
    try {
      const res = await fetchAdAccountCard(clientSlug);
      const data = res?.data || {};
      setStatus(data);
      onStatusChange?.(data);
    } catch {
      setStatus({ configured: false, submission: null, history: [], loadFailed: true });
    }
  }, [clientSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const submission = status?.submission || null;
  const lastClosed = status?.history?.[0] || null;
  const configured = status?.configured !== false;
  const ttlDays = status?.ttl_days || 14;

  const update = (key) => (e) => {
    const raw = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const value = key === 'card_number' ? formatCardNumber(raw)
      : key === 'cvc' ? digitsOnly(raw).slice(0, 4)
        : raw;
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      await submitAdAccountCard(clientSlug, {
        cardholder_name: form.cardholder_name,
        card_number: digitsOnly(form.card_number),
        exp_month: Number(form.exp_month),
        exp_year: Number(form.exp_year),
        cvc: digitsOnly(form.cvc),
        billing_zip: form.billing_zip,
        authorized: form.authorized,
      });
      setForm(EMPTY_FORM);
      setReplacing(false);
      toast.success('Card received. We will add it to your ad account, then delete it from our system.');
      await load();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === 'object' && !data.detail) {
        const next = {};
        Object.entries(data).forEach(([key, value]) => {
          next[key] = Array.isArray(value) ? value.join(' ') : String(value);
        });
        setErrors(next);
        toast.error('Please check the highlighted fields.');
      } else {
        toast.error(data?.detail || 'Could not save the card. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await withdrawAdAccountCard(clientSlug);
      toast.success('Card removed. Nothing is stored anymore.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove the card.');
    } finally {
      setWithdrawing(false);
    }
  };

  if (status === null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Checking ad account card…</Typography>
      </Stack>
    );
  }

  const showForm = configured && (!submission || replacing);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <LockIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={700}>Facebook Ad Account Card</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Facebook bills your ad spend directly, so we need a card to attach to the ad account that runs
        your campaigns. It is stored encrypted, our team views it once to add it to Facebook, and it is
        then permanently deleted from our system. Our service fee is invoiced separately.
      </Typography>

      {status?.loadFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>We could not check your card status right now. Please refresh the page.</Alert>
      )}
      {!configured && !status?.loadFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Card storage is not available at the moment. Your account manager will reach out to collect this.
        </Alert>
      )}

      {submission && !replacing && (
        <Alert
          severity={submission.status === 'revealed' ? 'info' : 'success'}
          variant="outlined"
          icon={<CheckCircleIcon />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={(
            <Stack direction="row" spacing={0.5}>
              <Button size="small" onClick={() => setReplacing(true)} sx={{ textTransform: 'none' }}>Replace</Button>
              <Button size="small" color="error" onClick={handleWithdraw} disabled={withdrawing} sx={{ textTransform: 'none' }}>
                {withdrawing ? 'Removing…' : 'Remove'}
              </Button>
            </Stack>
          )}
        >
          <strong>{brandLabel(submission.brand)} ending in {submission.last4}</strong> received {fmtDate(submission.submitted_at)}.{' '}
          {submission.status === 'revealed'
            ? 'Our team is adding it to your ad account right now.'
            : 'Our team will add it to your ad account and then delete it from our system.'}
        </Alert>
      )}

      {!submission && !replacing && lastClosed?.status === 'applied' && (
        <Alert severity="success" variant="outlined" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
          <strong>{brandLabel(lastClosed.brand)} ending in {lastClosed.last4}</strong> was added to your ad account
          on {fmtDate(lastClosed.applied_at)} and is no longer stored here. Need to switch cards? Submit a new one below.
        </Alert>
      )}
      {!submission && !replacing && lastClosed?.status === 'expired' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The card you submitted on {fmtDate(lastClosed.submitted_at)} was deleted after {ttlDays} days without being used.
          Please submit it again.
        </Alert>
      )}

      {showForm && (
        <Box component="form" onSubmit={handleSubmit} autoComplete="on" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7}>
              <TextField
                label="Name on card" value={form.cardholder_name} onChange={update('cardholder_name')}
                fullWidth required autoComplete="cc-name" error={!!errors.cardholder_name} helperText={errors.cardholder_name}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Billing ZIP" value={form.billing_zip} onChange={update('billing_zip')}
                fullWidth autoComplete="postal-code" inputProps={{ maxLength: 12 }}
                error={!!errors.billing_zip} helperText={errors.billing_zip}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Card number" value={form.card_number} onChange={update('card_number')}
                fullWidth required autoComplete="cc-number" placeholder="1234 5678 9012 3456"
                inputProps={{ inputMode: 'numeric', maxLength: 23, style: { fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 1 } }}
                error={!!errors.card_number} helperText={errors.card_number}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select label="Exp. month" value={form.exp_month} onChange={update('exp_month')}
                fullWidth required autoComplete="cc-exp-month" error={!!errors.exp_month} helperText={errors.exp_month}
              >
                {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select label="Exp. year" value={form.exp_year} onChange={update('exp_year')}
                fullWidth required autoComplete="cc-exp-year" error={!!errors.exp_year} helperText={errors.exp_year}
              >
                {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Security code (CVC)" value={form.cvc} onChange={update('cvc')}
                fullWidth required autoComplete="cc-csc" type="password"
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                error={!!errors.cvc} helperText={errors.cvc || '3 digits on the back, 4 on the front for American Express'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={form.authorized} onChange={update('authorized')} />}
                label={(
                  <Typography variant="body2">
                    I authorize Lead Gurus to add this card as the payment method on the Facebook ad account used for
                    my campaigns. Facebook will charge it for ad spend per my agreement.
                  </Typography>
                )}
              />
              {errors.authorized && (
                <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>{errors.authorized}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button type="submit" variant="contained" disabled={submitting || !form.authorized} startIcon={<LockIcon />} sx={{ textTransform: 'none' }}>
                  {submitting ? 'Saving securely…' : replacing ? 'Replace card' : 'Securely submit card'}
                </Button>
                {replacing && (
                  <Button onClick={() => { setReplacing(false); setForm(EMPTY_FORM); setErrors({}); }} sx={{ textTransform: 'none' }}>
                    Cancel
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Encrypted on arrival. Viewed once by our team to add it to Facebook, then deleted — automatically after {ttlDays} days if unused.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
